import { CellEventFormatterProps } from '../../utils'
import { useMemo } from 'react'
import { DefaultUpdateEvent } from './DefaultUpdateEvent'
import { GreenBold } from 'components/Tables/ActivityTableV2/CellEvent/CellEventFormatter'
import useApiService from 'services/api'
import { SalesStage } from 'models/sales_stages'
import { StagePill } from 'components/FbUI/StagePill'

export const DealUpdateEvent = ({ event }: CellEventFormatterProps) => {
  if (event.field_name === 'sales_stage_id') {
    return <DealUpdateSalesStageEvent event={event} />
  }
  if (event.field_name === 'account_owner_id') {
    return <DealUpdateAccountOwnerEvent event={event} />
  }

  return <DefaultUpdateEvent event={event} />
}

const DealUpdateSalesStageEvent = ({ event }: CellEventFormatterProps) => {
  const api = useApiService()
  const { data: salesStages } = api.useGetSalesStageOptions()
  const salesStagesMap = useMemo(() => {
    if (!salesStages) return {}
    return salesStages.reduce((acc: Record<string, SalesStage>, curr) => {
      if (!curr.original) return acc
      acc[curr.original.id] = curr.original
      return acc
    }, {})
  }, [salesStages])

  const stage = salesStagesMap[event.value as unknown as string]

  // Flex layout so a long stage pill truncates in place (…, with tooltip /
  // click-to-expand) instead of vanishing when the cell is narrow: the label is
  // shrink-0 and the pill is min-w-0 (shrinks + its name ellipsizes), rather
  // than being clipped wholesale by the cell's text-overflow.
  return (
    <span data-event-flex className="flex min-w-0 items-center gap-1">
      <span className="shrink-0">
        changed <GreenBold>sales stage</GreenBold> to
      </span>
      {stage ? (
        <StagePill stageId={stage.id} className="min-w-0 max-w-full" />
      ) : (
        <b>{String(event.value)}</b>
      )}
    </span>
  )
}

const DealUpdateAccountOwnerEvent = ({ event }: CellEventFormatterProps) => {
  const api = useApiService()
  const { data: users } = api.useGetUserOptions({ onlyActive: true })
  const usersMap = useMemo(() => {
    if (!users) return {}

    return users.reduce((acc: Record<string, any>, curr) => {
      if (!curr.original) return acc
      acc[curr.original.id] = curr.original
      return acc
    }, {})
  }, [users])

  const customFormatter = useMemo(() => {
    return (value: any) => {
      const user = usersMap[value]
      if (!user) return undefined
      return `${user.first_name} ${user.last_name}`
    }
  }, [event.field_name, usersMap])

  return (
    <DefaultUpdateEvent
      event={event}
      customFieldName="account owner"
      customFormatter={customFormatter}
    />
  )
}
